import type { EsConditionConfig } from "@/components/ConditionPageLayoutEs";
import {
  BRAIN_IMG,
  ES_REVIEWED_BY,
  ES_LAST_UPDATED,
  esBaseCrumbs,
} from "./esConditionShared";

export const esPtsd: EsConditionConfig = {
  canonical: "/es/ptsd-treatment-coral-springs-fl",
  badge: "Terapia con ketamina IV",
  title: "Tratamiento del TEPT con ketamina",
  subtitle:
    "La infusión de ketamina IV puede ayudar a aliviar los síntomas del trastorno de estrés postraumático cuando los tratamientos convencionales no son suficientes, en Coral Springs, FL.",
  introHeading:
    "Alivio de los síntomas del TEPT con tratamiento de ketamina IV en Coral Springs, FL",
  introText: [
    "El trastorno de estrés postraumático (TEPT) puede causar síntomas graves y duraderos que interrumpen su vida diaria: recuerdos intrusivos, hiperalerta, evitación y alteraciones del sueño. Cuando los medicamentos y la psicoterapia convencionales no ofrecen suficiente alivio, la infusión de ketamina IV puede ayudar.",
    "Ofrecemos tratamiento con ketamina IV como una opción complementaria basada en evidencia para adultos con TEPT en Coral Springs, FL.",
  ],
  whatIsTitle: "¿Qué es la ketamina para el TEPT?",
  whatIsText: [
    "La ketamina, utilizada durante mucho tiempo como anestésico, ha mostrado en investigaciones recientes un potencial para reducir rápidamente los síntomas del TEPT, incluso en pacientes que no han respondido a los tratamientos estándar.",
    "La infusión de ketamina IV en dosis baja actúa sobre el glutamato y otros receptores del cerebro implicados en la respuesta al miedo y la memoria, con un inicio de acción que puede ser notablemente más rápido que el de los antidepresivos orales.",
  ],
  howItHelpsTitle: "¿Cómo ayuda la ketamina con el TEPT?",
  howItHelpsText: [
    "El TEPT puede dejar al cerebro en un estado de hiperalerta, con sobreestimulación de la amígdala (el centro del miedo) y menor regulación de la corteza prefrontal. La ketamina ayuda a restablecer el equilibrio al modular la actividad en los circuitos implicados en el procesamiento del miedo y el trauma.",
    "Se dirige a los mecanismos neurales subyacentes del trauma, ofreciendo una alternativa para los pacientes que no han encontrado alivio con los tratamientos estándar.",
  ],
  treatmentExperienceTitle: "¿Cómo es una sesión de tratamiento con ketamina?",
  treatmentExperienceText: [
    "Cada sesión se realiza en nuestro centro en Coral Springs, FL, con supervisión médica continua. La infusión de dosis baja se administra lentamente durante aproximadamente 40 a 60 minutos en un entorno seguro y controlado.",
    "Nuestro equipo clínico está presente durante toda la sesión para garantizar su seguridad y comodidad.",
  ],
  benefitsTitle: "Beneficios del tratamiento con ketamina para el TEPT",
  benefits: [
    {
      title: "Alivio rápido de los síntomas",
      desc: "La ketamina puede reducir los síntomas del TEPT en horas o días, en lugar de las semanas que suelen necesitar los ajustes de medicación.",
    },
    {
      title: "Se dirige a los circuitos del miedo",
      desc: "Modula la actividad en las regiones cerebrales implicadas en el procesamiento del trauma.",
    },
    {
      title: "Enfoque complementario",
      desc: "Se puede combinar con la psicoterapia y el manejo de medicación como parte de un plan integral.",
    },
    {
      title: "Cuidado ambulatorio",
      desc: "El tratamiento se realiza en consultorio, sin hospitalización.",
    },
  ],
  imageUrl: BRAIN_IMG,
  imageAlt: "Tratamiento del TEPT en Coral Springs, FL",
  activeConditionHref: "/es/ptsd-treatment-coral-springs-fl",
  seoTitle: "Ketamina para el TEPT en Coral Springs, FL | Neuro Wellness",
  seoDescription:
    "Infusión de ketamina IV para los síntomas del trastorno de estrés postraumático en Coral Springs, FL. Cuidado complementario basado en evidencia.",
  keywords:
    "ketamina TEPT Coral Springs, tratamiento TEPT Coral Springs FL, trastorno de estrés postraumático ketamina",
  reviewedBy: ES_REVIEWED_BY,
  lastUpdated: ES_LAST_UPDATED,
  breadcrumbs: esBaseCrumbs("TEPT", "/es/ptsd-treatment-coral-springs-fl"),
};

export const esOcd: EsConditionConfig = {
  canonical: "/es/ocd-treatment-coral-springs-fl",
  badge: "Terapia con ketamina IV",
  title: "Tratamiento del TOC con ketamina",
  subtitle:
    "Cuando los medicamentos y la terapia estándar no son suficientes, la infusión de ketamina IV puede ofrecer alivio para los síntomas graves del TOC en Coral Springs, FL.",
  introHeading:
    "Alivio del TOC con tratamiento de ketamina IV en Coral Springs, FL",
  introText: [
    "El trastorno obsesivo-compulsivo (TOC) puede ser profundamente incapacitante, con pensamientos intrusivos y comportamientos compulsivos que consumen horas cada día. Cuando los inhibidores de la recaptación de serotonina y la terapia de exposición no ofrecen suficiente alivio, la infusión de ketamina IV puede ayudar.",
    "Ofrecemos tratamiento con ketamina IV como una opción complementaria para adultos con TOC resistente al tratamiento en Coral Springs, FL.",
  ],
  whatIsTitle: "¿Qué es la ketamina para el TOC?",
  whatIsText: [
    "La ketamina ha mostrado en investigaciones recientes un potencial para reducir los síntomas obsesivo-compulsivos, incluso en pacientes que no han respondido a los tratamientos estándar.",
    "La infusión de ketamina IV en dosis baja actúa sobre el glutamato y otros receptores del cerebro implicados en los circuitos del TOC, con un inicio de acción que puede ser más rápido que el de muchos medicamentos orales.",
  ],
  howItHelpsTitle: "¿Cómo ayuda la ketamina con el TOC?",
  howItHelpsText: [
    "El TOC implica hiperactividad en circuitos cerebrales específicos, particularmente entre los ganglios basales y la corteza prefrontal. La ketamina modula la actividad del glutamato en estos circuitos, lo que puede ayudar a reducir la intensidad de los pensamientos obsesivos y los comportamientos compulsivos.",
    "Se utiliza como parte de un plan integral, junto con la psicoterapia (como la terapia de exposición y prevención de respuesta) y el manejo de medicación coordinado por su psiquiatra.",
  ],
  treatmentExperienceTitle: "¿Cómo es una sesión de tratamiento con ketamina?",
  treatmentExperienceText: [
    "Cada sesión se realiza en nuestro centro en Coral Springs, FL, con supervisión médica continua. La infusión de dosis baja se administra lentamente durante aproximadamente 40 a 60 minutos en un entorno seguro y controlado.",
    "Nuestro equipo clínico está presente durante toda la sesión para garantizar su seguridad y comodidad.",
  ],
  benefitsTitle: "Beneficios del tratamiento con ketamina para el TOC",
  benefits: [
    {
      title: "Alivio potencialmente rápido",
      desc: "La ketamina puede reducir los síntomas del TOC en horas o días, en lugar de las semanas que suelen necesitar los ajustes de medicación.",
    },
    {
      title: "Se dirige a los circuitos del TOC",
      desc: "Modula la actividad del glutamato en los circuitos cerebrales implicados en los síntomas obsesivo-compulsivos.",
    },
    {
      title: "Enfoque complementario",
      desc: "Se puede combinar con la psicoterapia y el manejo de medicación.",
    },
    {
      title: "Cuidado ambulatorio",
      desc: "El tratamiento se realiza en consultorio, sin hospitalización.",
    },
  ],
  imageUrl: BRAIN_IMG,
  imageAlt: "Tratamiento del TOC en Coral Springs, FL",
  activeConditionHref: "/es/ocd-treatment-coral-springs-fl",
  seoTitle: "Ketamina para el TOC en Coral Springs, FL | Neuro Wellness",
  seoDescription:
    "Infusión de ketamina IV para el trastorno obsesivo-compulsivo resistente al tratamiento en Coral Springs, FL. Cuidado complementario basado en evidencia.",
  keywords:
    "ketamina TOC Coral Springs, tratamiento TOC Coral Springs FL, trastorno obsesivo compulsivo ketamina",
  reviewedBy: ES_REVIEWED_BY,
  lastUpdated: ES_LAST_UPDATED,
  breadcrumbs: esBaseCrumbs("TOC", "/es/ocd-treatment-coral-springs-fl"),
};

export const esSuicidalIdeation: EsConditionConfig = {
  canonical: "/es/suicidal-ideation-treatment-coral-springs-fl",
  badge: "Terapia con ketamina IV",
  title: "Tratamiento de la ideación suicida con ketamina",
  subtitle:
    "La infusión de ketamina IV puede ofrecer alivio rápido de la ideación suicida aguda en un entorno seguro y supervisado en Coral Springs, FL.",
  introHeading:
    "Alivio rápido de la ideación suicida con tratamiento de ketamina IV en Coral Springs, FL",
  introText: [
    "Si usted o alguien que conoce está en crisis o tiene pensamientos suicidas, por favor llame o envíe un mensaje de texto al 988 (Línea de Suicidio y Crisis, disponible 24/7) o al 911 de inmediato. Esta página describe una opción de tratamiento; no es ayuda de emergencia.",
    "Para los adultos con ideación suicida aguda que no responde a otros tratamientos, la infusión de ketamina IV puede ofrecer un alivio rápido en un entorno clínico seguro y supervisado como parte de un plan integral de seguridad y cuidado en Coral Springs, FL.",
  ],
  whatIsTitle: "¿Qué es la ketamina para la ideación suicida?",
  whatIsText: [
    "La ketamina ha mostrado en investigaciones un potencial para reducir rápidamente la ideación suicida en algunos pacientes, incluso en casos resistentes al tratamiento.",
    "La infusión de ketamina IV en dosis baja actúa sobre el glutamato y otros receptores del cerebro implicados en el estado de ánimo y la respuesta al estrés, con un inicio de acción que puede ser notablemente más rápido que el de los antidepresivos orales.",
  ],
  howItHelpsTitle: "¿Cómo ayuda la ketamina con la ideación suicida?",
  howItHelpsText: [
    "La ketamina puede reducir rápidamente la intensidad de los pensamientos suicidas en algunos pacientes, ofreciendo una ventana de alivio durante la cual se puede iniciar o intensificar el tratamiento a más largo plazo.",
    "El tratamiento siempre se coordina como parte de un plan integral de seguridad, junto con la psicoterapia, el manejo de medicación y un plan de seguridad, bajo la supervisión de su psiquiatra.",
  ],
  treatmentExperienceTitle: "¿Cómo es una sesión de tratamiento con ketamina?",
  treatmentExperienceText: [
    "Cada sesión se realiza en nuestro centro en Coral Springs, FL, con supervisión médica continua en un entorno seguro y controlado. La infusión de dosis baja se administra lentamente.",
    "Nuestro equipo clínico está presente durante toda la sesión para garantizar su seguridad, y se coordina un plan de seguimiento y seguridad después del tratamiento.",
  ],
  benefitsTitle:
    "Beneficios del tratamiento con ketamina para la ideación suicida",
  benefits: [
    {
      title: "Alivio rápido",
      desc: "La ketamina puede reducir la ideación suicida en horas o días, en lugar de las semanas que suelen necesitar los antidepresivos orales.",
    },
    {
      title: "Entorno seguro y supervisado",
      desc: "El tratamiento se realiza con supervisión médica continua como parte de un plan de seguridad.",
    },
    {
      title: "Enfoque integral",
      desc: "Se coordina con psicoterapia, manejo de medicación y un plan de seguridad.",
    },
    {
      title: "Cuidado ambulatorio",
      desc: "El tratamiento se realiza en consultorio, sin hospitalización (para pacientes ambulatorios estables).",
    },
  ],
  imageUrl: BRAIN_IMG,
  imageAlt: "Tratamiento de la ideación suicida en Coral Springs, FL",
  activeConditionHref: "/es/suicidal-ideation-treatment-coral-springs-fl",
  seoTitle:
    "Ketamina para la ideación suicida en Coral Springs, FL | Neuro Wellness",
  seoDescription:
    "Infusión de ketamina IV para la ideación suicida aguda resistente al tratamiento en Coral Springs, FL. Cuidado supervisado como parte de un plan integral de seguridad.",
  keywords:
    "ketamina ideación suicida Coral Springs, tratamiento ideación suicida Coral Springs FL, ketamina pensamientos suicidas",
  reviewedBy: ES_REVIEWED_BY,
  lastUpdated: ES_LAST_UPDATED,
  breadcrumbs: esBaseCrumbs(
    "Ideación suicida",
    "/es/suicidal-ideation-treatment-coral-springs-fl",
  ),
};
